import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminApi, ProductKey } from "@/api/admin.api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { FirstRoundPassPanel } from "./FirstRoundPassPanel";
import { useConfirm } from "./ui/ConfirmDialog";

type Props = {
  entityType: "user" | "recruiter";
  entityId: string;
  product: ProductKey;
  subscriptions: any[];
  stripe: any;
  organizations?: Array<{ id: string; name: string }>;
};

export function AdminEntityActions({
  entityType,
  entityId,
  product,
  subscriptions,
  stripe,
  organizations = [],
}: Props) {
  const queryClient = useQueryClient();
  const [confirm, confirmDialog] = useConfirm();
  const [selectedSubscription, setSelectedSubscription] = useState("");
  const firstRound = entityType === "user" && product === "THERESUME";
  const [selectedPlan, setSelectedPlan] = useState("");
  const [planReason, setPlanReason] = useState("");
  const [featureKey, setFeatureKey] = useState("");
  const [additionalUnits, setAdditionalUnits] = useState(0);
  const [limitReason, setLimitReason] = useState("");
  const [scope, setScope] = useState<"individual" | "organization">("individual");
  const [organizationId, setOrganizationId] = useState("");
  const [expiresInDays, setExpiresInDays] = useState("");
  const [refundOpen, setRefundOpen] = useState(false);
  const [paymentIntentId, setPaymentIntentId] = useState("");
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [message, setMessage] = useState("");

  const { data: plans = [] } = useQuery({
    queryKey: ["admin-action-plans", product],
    queryFn: () => adminApi.listPlans(product),
  });
  const { data: features = [] } = useQuery({
    queryKey: ["admin-action-features", product],
    queryFn: () => adminApi.listFeatures(product),
  });
  const productSubscriptions = useMemo(
    () => subscriptions.filter((subscription) => subscription.plan?.product === product),
    [product, subscriptions]
  );

  const invalidate = async () => {
    await queryClient.invalidateQueries({
      queryKey: [
        entityType === "user" ? "admin-user-overview" : "admin-recruiter-overview",
        entityId,
      ],
    });
  };

  const planMutation = useMutation({
    mutationFn: async () => {
      if (
        !(await confirm({
          title: "Change this subscription's plan?",
          description: "This changes a live subscription in Stripe, with proration.",
          confirmLabel: "Change plan",
        }))
      ) {
        throw new Error("Action cancelled");
      }
      const dto = {
        subscriptionId: selectedSubscription,
        newPlanId: selectedPlan,
        reason: planReason,
      };
      return entityType === "user"
        ? adminApi.changeUserPlan(entityId, dto)
        : adminApi.changeRecruiterPlan(entityId, dto);
    },
    onSuccess: async () => {
      setMessage(
        "Plan change submitted successfully. Stripe webhooks will synchronize the database."
      );
      setSelectedPlan("");
      setPlanReason("");
      await invalidate();
    },
    onError: (error: Error) => {
      if (error.message !== "Action cancelled") setMessage(`Plan change failed: ${error.message}`);
    },
  });

  const limitMutation = useMutation({
    mutationFn: async () => {
      if (
        !(await confirm({
          title: "Adjust this feature's capacity?",
          description: `Adds ${additionalUnits} unit(s) of ${featureKey} in production.`,
          confirmLabel: "Apply",
        }))
      ) {
        throw new Error("Action cancelled");
      }
      if (entityType === "user") {
        return adminApi.adjustUserLimit(entityId, {
          featureKey,
          additionalUnits,
          reason: limitReason,
          product,
        });
      }
      return adminApi.adjustRecruiterLimit(entityId, {
        featureKey,
        additionalUnits,
        reason: limitReason,
        scope,
        organizationId: scope === "organization" ? organizationId : undefined,
        expiresInDays: expiresInDays ? Number(expiresInDays) : undefined,
      });
    },
    onSuccess: async () => {
      setMessage("Feature capacity adjusted successfully.");
      setAdditionalUnits(0);
      setLimitReason("");
      await invalidate();
    },
    onError: (error: Error) => {
      if (error.message !== "Action cancelled")
        setMessage(`Limit adjustment failed: ${error.message}`);
    },
  });

  const refundMutation = useMutation({
    mutationFn: async () => {
      if (
        !(await confirm({
          title: "Issue this refund in Stripe?",
          description: "The money goes back to the customer. This can't be undone from here.",
          confirmLabel: "Issue refund",
          destructive: true,
          typeToConfirm: "refund",
        }))
      ) {
        throw new Error("Action cancelled");
      }
      const dto = {
        paymentIntentId,
        amount: refundAmount ? Number(refundAmount) : undefined,
        reason: refundReason,
      };
      return entityType === "user"
        ? adminApi.refundUserPayment(entityId, dto)
        : adminApi.refundRecruiterPayment(entityId, dto);
    },
    onSuccess: async () => {
      setMessage("Refund issued successfully.");
      setRefundOpen(false);
      setPaymentIntentId("");
      setRefundAmount("");
      setRefundReason("");
      await invalidate();
    },
    onError: (error: Error) => {
      if (error.message !== "Action cancelled") setMessage(`Refund failed: ${error.message}`);
    },
  });

  return (
    <section className="space-y-4 rounded-sm border-2 border-amber-300 bg-amber-50/40 p-4">
      {confirmDialog}
      <div>
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-slate-950">Administrative Actions</h2>
          <Badge variant="warning">Modifies production</Badge>
        </div>
        <p className="mt-1 text-sm text-slate-600">
          These operations affect billing or feature capacity and are recorded in the admin audit
          log.
        </p>
        {message && (
          <p className="mt-2 rounded-sm bg-white p-2 text-sm text-slate-700">{message}</p>
        )}
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        {/* theResume users are on First Round passes: one-off, no Stripe subscription to change, and
            limits that "Adjust Feature Limit" can't reach. */}
        {firstRound ? (
          <FirstRoundPassPanel userId={entityId} />
        ) : (
          <>
            <Card>
              <CardHeader>
                <CardTitle>Change Subscription Plan</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <FieldLabel label="Subscription">
                  <select
                    aria-label="Subscription"
                    className="h-10 w-full rounded-sm border border-slate-300 bg-white px-3 text-sm"
                    value={selectedSubscription}
                    onChange={(event) => setSelectedSubscription(event.target.value)}
                  >
                    <option value="">Select subscription</option>
                    {productSubscriptions.map((subscription) => (
                      <option key={subscription.id} value={subscription.id}>
                        {subscription.plan.displayName} ·{" "}
                        {subscription.organizationId ? "Organization" : "Individual"}
                      </option>
                    ))}
                  </select>
                </FieldLabel>
                <FieldLabel label="New plan">
                  <select
                    aria-label="New plan"
                    className="h-10 w-full rounded-sm border border-slate-300 bg-white px-3 text-sm"
                    value={selectedPlan}
                    onChange={(event) => setSelectedPlan(event.target.value)}
                  >
                    <option value="">Select plan</option>
                    {plans.map((plan: any) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.displayName} · {plan.tier}
                      </option>
                    ))}
                  </select>
                </FieldLabel>
                <FieldLabel label="Reason">
                  <Input
                    value={planReason}
                    onChange={(event) => setPlanReason(event.target.value)}
                  />
                </FieldLabel>
                <Button
                  disabled={
                    !selectedSubscription ||
                    !selectedPlan ||
                    !planReason.trim() ||
                    planMutation.isPending
                  }
                  onClick={() => planMutation.mutate()}
                >
                  {planMutation.isPending ? "Changing..." : "Change Plan"}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Adjust Feature Limit</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {entityType === "recruiter" && (
                  <FieldLabel label="Billing context">
                    <select
                      aria-label="Billing context"
                      className="h-10 w-full rounded-sm border border-slate-300 bg-white px-3 text-sm"
                      value={scope === "individual" ? "individual" : organizationId}
                      onChange={(event) => {
                        if (event.target.value === "individual") {
                          setScope("individual");
                          setOrganizationId("");
                        } else {
                          setScope("organization");
                          setOrganizationId(event.target.value);
                        }
                      }}
                    >
                      <option value="individual">Individual recruiter</option>
                      {organizations.map((organization) => (
                        <option key={organization.id} value={organization.id}>
                          Organization · {organization.name}
                        </option>
                      ))}
                    </select>
                  </FieldLabel>
                )}
                <FieldLabel label="Feature">
                  <select
                    aria-label="Feature"
                    className="h-10 w-full rounded-sm border border-slate-300 bg-white px-3 text-sm"
                    value={featureKey}
                    onChange={(event) => setFeatureKey(event.target.value)}
                  >
                    <option value="">Select feature</option>
                    {features.map((feature: any) => (
                      <option key={feature.id} value={feature.key}>
                        {feature.name || feature.key}
                      </option>
                    ))}
                  </select>
                </FieldLabel>
                <FieldLabel
                  label={entityType === "recruiter" ? "Additional capacity" : "Limit adjustment"}
                >
                  <Input
                    type="number"
                    min={entityType === "recruiter" ? 1 : undefined}
                    value={additionalUnits}
                    onChange={(event) => setAdditionalUnits(Number(event.target.value))}
                  />
                </FieldLabel>
                {entityType === "recruiter" && (
                  <FieldLabel label="Expires in days (optional)">
                    <Input
                      type="number"
                      min={1}
                      value={expiresInDays}
                      onChange={(event) => setExpiresInDays(event.target.value)}
                    />
                  </FieldLabel>
                )}
                <FieldLabel label="Reason">
                  <Input
                    value={limitReason}
                    onChange={(event) => setLimitReason(event.target.value)}
                  />
                </FieldLabel>
                <Button
                  disabled={
                    !featureKey ||
                    additionalUnits === 0 ||
                    (entityType === "recruiter" && additionalUnits < 1) ||
                    !limitReason.trim() ||
                    (scope === "organization" && !organizationId) ||
                    limitMutation.isPending
                  }
                  onClick={() => limitMutation.mutate()}
                >
                  {limitMutation.isPending ? "Applying..." : "Apply Adjustment"}
                </Button>
              </CardContent>
            </Card>
          </>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Issue Refund</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p className="text-slate-600">
              Stripe status: {stripe?.available ? "Available" : "Unavailable"}. Refunds require a
              payment intent ID.
            </p>
            <p className="text-slate-500">Recent invoices: {stripe?.recentInvoices?.length || 0}</p>
            <Button variant="destructive" onClick={() => setRefundOpen(true)}>
              Issue Refund
            </Button>
          </CardContent>
        </Card>
      </div>

      <Dialog open={refundOpen} onOpenChange={setRefundOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Issue Stripe Refund</DialogTitle>
            <DialogDescription>
              Enter the payment intent, optional partial amount, and a mandatory audit reason.
            </DialogDescription>
          </DialogHeader>
          <FieldLabel label="Payment intent ID">
            <Input
              value={paymentIntentId}
              onChange={(event) => setPaymentIntentId(event.target.value)}
            />
          </FieldLabel>
          <FieldLabel label="Amount (optional)">
            <Input
              type="number"
              min={0}
              value={refundAmount}
              onChange={(event) => setRefundAmount(event.target.value)}
            />
          </FieldLabel>
          <FieldLabel label="Reason">
            <Input value={refundReason} onChange={(event) => setRefundReason(event.target.value)} />
          </FieldLabel>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!paymentIntentId.trim() || !refundReason.trim() || refundMutation.isPending}
              onClick={() => refundMutation.mutate()}
            >
              {refundMutation.isPending ? "Refunding..." : "Confirm Refund"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function FieldLabel({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1 text-sm font-medium text-slate-700">
      <span>{label}</span>
      {children}
    </label>
  );
}
